import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {Form, Dropdown} from 'semantic-ui-react';
import months from '../../../months';
import {useReport} from '../../../providers/ReportProvider';
import {useCustomers} from '../../../providers/CustomerProvider';
import {SelectCustomerModal} from './SelectCustomerModal';
import {SyrxReportForm} from "./Forms/SyrxReportForm";
import {useAuth} from "../../../providers/AuthenticationProvider";

const WEB_PAGE = 'Reports';

export const ReportCreate = () => {
  const defaultTemplateId = 1;
  const {createReport} = useReport();
  const {isAccountExecutive, checkAuth, getUserType} = useAuth();
  const {customersList, getAllCustomers} = useCustomers();
  const [errors] = useState();
  const [loading, setLoading] = useState(false);
  const [reportTemplateId, setReportTemplateId] = useState(defaultTemplateId);
  const [customer, setCustomer] = useState(false);
  const [reportName, setReportName] = useState('');
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportStartMonth, setReportStartMonth] = useState(0);
  const [reportEndMonth, setReportEndMonth] = useState(11);
  const [benchmarkYear, setBenchmarkYear] = useState(new Date().getFullYear() - 1);
  const [benchmarkStartMonth, setBenchmarkStartMonth] = useState(0);
  const [benchmarkEndMonth, setBenchmarkEndMonth] = useState(11);
  const [blendedElectricRate, setBlendedElectricRate] = useState(0.07);
  const [blendedThermalRate, setBlendedThermalRate] = useState(5.6);
  const [blendedThermalUnit, setBlendedThermalUnit] = useState('$/mcf');
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [reportType, setReportType] = useState('energy');
  const navigate = useNavigate();

  checkAuth(getUserType(), WEB_PAGE) && navigate('/');

  const reportTypeOptions = useMemo(() => {
    const options = [];
    // if (!isAccountExecutive()) {
    //   options.push({key: '1', text: 'Syrx Report', value: 'syrx'});
    // }
    options.push({key: '2', text: 'Energy Audit Report', value: 'energy'});
    return options;
  }, []);

  useEffect(() => {
    getAllCustomers();
  }, [getAllCustomers]);

  const handleSubmit = e => {
    console.log(e);
    setLoading(true);
    createReport(
      reportName,
      reportYear,
      reportStartMonth,
      reportEndMonth,
      benchmarkYear,
      benchmarkStartMonth,
      benchmarkEndMonth,
      blendedElectricRate,
      blendedThermalRate,
      blendedThermalUnit,
      equipmentIds,
      customer.id,
      reportTemplateId
    ).then(data => {
      setLoading(false);
      navigate(`/reports?customerId=${customer.id}`);
    });
  };

  const selectReportTemplate = useCallback(
    selectedTemplate => {
      setReportTemplateId(selectedTemplate.id);
      setReportName(selectedTemplate.name);
    },
    [setReportTemplateId, setReportName]
  );

  return (
    <div>
      <div className="pageHeader">
        <h1>Create New Report</h1>
      </div>

      <Form style={{ maxWidth: '1000px' }}>
        <Form.Group widths="equal">
          <Form.Field
            label="Report Type"
            control={Dropdown}
            fluid
            selection
            value={reportType}
            onChange={(e, selection) => setReportType(selection.value)}
            options={reportTypeOptions}
            style={{ height: '38px' }}
          />
          <Form.Field
            label="Customer"
            control={Dropdown}
            fluid
            selection
            search
            options={customersList.map(x => {
              const text =
                x?.firstname && x?.lastname
                  ? `${x?.companyName} (${x?.firstname} ${x?.lastname})`
                  : x?.companyName;
              return {key: x?.id, text, value: x?.id};
            })}
            value={customer?.id || null}
            style={{ height: '38px' }}
            onChange={(e, selected) => {
              const c = customersList.find(x => x.id === selected.value);
              setCustomer(c);
            }}
          />
        </Form.Group>
      </Form>

      {reportType === 'syrx' && <SyrxReportForm customer={customer} />}

      {reportType === 'energy' && <>
        <SelectCustomerModal
          customer={customer}
          onEquipmentSelect={e => setEquipmentIds(e)}
          onReportTemplateSelect={rt => selectReportTemplate(rt)}
          defaultTemplateId={defaultTemplateId}
          selectedReportTemplateId={reportTemplateId}
        />

        {customer && reportTemplateId && (
          <Form onSubmit={handleSubmit} error={errors ? true : false} style={{ maxWidth: '1000px' }}>
            <Form.Group>
              <Form.Input
                width={16}
                type="input"
                label="Report Name"
                value={reportName}
                required
                onChange={e => setReportName(e.target.value)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Report Year"
                type="number"
                min={1900}
                step={1}
                value={reportYear}
                required
                onChange={e => setReportYear(e.target.value)}
              />
              <Form.Input
                label="Benchmark Year"
                type="number"
                min={1900}
                step={1}
                value={benchmarkYear}
                required
                onChange={e => setBenchmarkYear(e.target.value)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field
                control={Dropdown}
                fluid
                selection
                options={months}
                value={reportStartMonth}
                label="Report Start Month"
                onChange={(e, selection) => setReportStartMonth(selection.value)}
                required
                style={{height: '38px', color: 'black'}}
              />
              <Form.Field
                control={Dropdown}
                fluid
                selection
                options={months}
                value={benchmarkStartMonth}
                label="Benchmark Start Month"
                onChange={(e, selection) => setBenchmarkStartMonth(selection.value)}
                required
                style={{height: '38px', color: 'black'}}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field
                control={Dropdown}
                fluid
                selection
                search
                options={months}
                value={reportEndMonth}
                label="Report End Month"
                onChange={(e, selection) => setReportEndMonth(selection.value)}
                required
                style={{height: '38px'}}
              />
              <Form.Field
                control={Dropdown}
                fluid
                selection
                search
                options={months}
                value={benchmarkEndMonth}
                label="Benchmark End Month"
                onChange={(e, selection) => setBenchmarkEndMonth(selection.value)}
                required
                style={{height: '38px'}}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Blended Electric Rate ($/kWh)"
                type="number"
                value={blendedElectricRate}
                step={0.01}
                onChange={e => setBlendedElectricRate(e.target.value)}
                required
              />
              <Form.Input
                label="Blended Thermal Rate"
                type="number"
                value={blendedThermalRate}
                step={0.01}
                onChange={e => setBlendedThermalRate(e.target.value)}
                required
              />
              <Form.Field
                control={Dropdown}
                fluid
                selection
                search
                options={[
                  {key: '$/therm', value: '$/therm', text: '$/Therm'},
                  {key: '$/mcf', value: '$/mcf', text: '$/MCF'},
                ]}
                value={blendedThermalUnit}
                label="Blended Thermal Unit"
                onChange={(e, selection) => setBlendedThermalUnit(selection.value)}
                required
                style={{height: '38px'}}
              />
            </Form.Group>
            <Form.Group>
              <Form.Button size="large" loading={loading} color="blue">
                Submit
              </Form.Button>
              <Form.Button
                size="large"
                onClick={e => {
                  e.preventDefault();
                  navigate(-1);
                }}
              >
                Cancel
              </Form.Button>
            </Form.Group>
          </Form>
        )}
      </>}
    </div>
  );
};
